/**
 * BucketColumn — board column container for a single renewal bucket.
 * Ported from v0 components/brokly/board-column.tsx (BoardColumn).
 *
 * Colour discipline: column header dot is NEUTRAL (bg-muted-foreground/50).
 * No bucket gets an urgency colour — only individual cards (RenewalCard) do.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §41 (R3)]
 */

import { chf } from '../../../lib/format';
import { RenewalCard } from './RenewalCard';
import { sortByUrgency } from '../mock';
import type { RenewalBucket } from '../types';
import type { RenewalBoardItemVM } from '../types';

interface BucketColumnProps {
  bucket: RenewalBucket;
  label: string;
  items: RenewalBoardItemVM[];
  onKundeActivate: (item: RenewalBoardItemVM) => void;
}

export function BucketColumn({ label, items, onKundeActivate }: BucketColumnProps) {
  const sorted = sortByUrgency(items);

  // Prämien sum for the column header (neutral display)
  const praemieSum = items.reduce((acc, it) => {
    return it.praemie !== null ? acc + Number(it.praemie) : acc;
  }, 0);
  const hasPraemie = items.some((it) => it.praemie !== null);

  return (
    <div className="flex flex-col gap-2 min-w-0">
      {/* Column header — neutral chrome: dot + label + count + optional Prämiensumme */}
      <div className="flex items-center gap-1.5 pb-1">
        {/* Neutral dot — NOT urgency coloured */}
        <span className="size-2 shrink-0 rounded-full bg-muted-foreground/50" />
        <span className="text-xs font-semibold text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground tabular-nums">({items.length})</span>
        {hasPraemie && items.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground mono-num">
            · {chf(praemieSum)}
          </span>
        )}
      </div>

      {/* Cards */}
      {sorted.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Keine Renewals in diesem Zeitfenster.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {sorted.map((item) => (
            <RenewalCard
              key={item.policy_id}
              item={item}
              onKundeActivate={
                item.kundeId ? () => onKundeActivate(item) : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
